"use client";

import { useCallback, useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

const ROLES = [
  { key: "founder", label: "Founder", bpsKey: "founderBps" },
  { key: "platformSalesperson", label: "Platform Salesperson", bpsKey: "salesBps" },
  { key: "bookkeeper", label: "Bookkeeper", bpsKey: "bookkeeperBps" },
  { key: "it", label: "IT", bpsKey: "itBps" },
] as const;

type Shares = Record<"founderBps" | "salesBps" | "bookkeeperBps" | "itBps", number>;

export default function RevenueDistributorAdminPage() {
  const { getAccessToken } = usePrivy();
  const [wallets, setWallets] = useState<Record<string, string>>({});
  const [walletInputs, setWalletInputs] = useState<Record<string, string>>({});
  const [shares, setShares] = useState<Shares | null>(null);
  const [shareInputs, setShareInputs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const call = useCallback(
    async (path: string, body?: unknown) => {
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in again to continue.");
      const response = await fetch(`${API_BASE_URL}/api/revenue-distributor${path}`, {
        method: body ? "POST" : "GET",
        cache: "no-store",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: body ? JSON.stringify(body) : undefined,
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Request failed.");
      return payload;
    },
    [getAccessToken]
  );

  const load = useCallback(async () => {
    try {
      const data = await call("");
      setWallets(data.wallets);
      setShares(data.shares);
      setShareInputs(
        Object.fromEntries(Object.entries(data.shares).map(([k, v]) => [k, String(v)]))
      );
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load settings.");
    }
  }, [call]);

  useEffect(() => {
    load();
  }, [load]);

  async function run(id: string, path: string, body: unknown) {
    setBusy(id);
    setError(null);
    setMessage(null);
    try {
      const result = await call(path, body);
      setMessage(`Updated on-chain. Transaction ${result.transactionHash}`);
      await load();
    } catch (runError) {
      setError(runError instanceof Error ? runError.message : "Update failed.");
    } finally {
      setBusy(null);
    }
  }

  const total = ROLES.reduce((sum, r) => sum + (Number(shareInputs[r.bpsKey]) || 0), 0);

  return (
    <main className="mx-auto max-w-4xl space-y-6 p-8 text-white">
      <h1 className="text-2xl font-bold">Revenue Distributor</h1>
      <p className="text-sm text-slate-400">
        Each change is sent on-chain to the RevenueDistributor contract. Changes apply to future deposits only.
      </p>

      {message && <p className="text-sm text-emerald-300 break-all">{message}</p>}
      {error && <p className="text-sm text-red-300">{error}</p>}

      <section className="space-y-4 rounded-xl border border-slate-700 p-4">
        <h2 className="font-semibold">Wallets</h2>
        {ROLES.map((role) => (
          <div key={role.key} className="space-y-1">
            <div className="text-sm">
              {role.label}: <span className="font-mono text-xs text-slate-300">{wallets[role.key] ?? "..."}</span>
            </div>
            <div className="flex gap-2">
              <input
                value={walletInputs[role.key] ?? ""}
                onChange={(e) => setWalletInputs({ ...walletInputs, [role.key]: e.target.value })}
                placeholder="New wallet address (0x...)"
                className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs"
              />
              <button
                type="button"
                disabled={busy !== null || !walletInputs[role.key]?.trim()}
                onClick={() => run(role.key, `/wallets/${role.key}`, { wallet: walletInputs[role.key].trim() })}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
              >
                {busy === role.key ? "Submitting..." : "Update"}
              </button>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-3 rounded-xl border border-slate-700 p-4">
        <h2 className="font-semibold">Revenue shares (basis points, 100 = 1%)</h2>
        {shares &&
          ROLES.map((role) => (
            <label key={role.key} className="flex items-center justify-between gap-4 text-sm">
              {role.label}
              <input
                type="number"
                min={0}
                value={shareInputs[role.bpsKey] ?? ""}
                onChange={(e) => setShareInputs({ ...shareInputs, [role.bpsKey]: e.target.value })}
                className="w-28 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs"
              />
            </label>
          ))}
        <p className={`text-xs ${total === 10000 ? "text-emerald-300" : "text-amber-300"}`}>
          Total: {total} / 10000
        </p>
        <button
          type="button"
          disabled={busy !== null || total !== 10000}
          onClick={() =>
            run("shares", "/shares", {
              founderBps: Number(shareInputs.founderBps),
              salesBps: Number(shareInputs.salesBps),
              bookkeeperBps: Number(shareInputs.bookkeeperBps),
              itBps: Number(shareInputs.itBps),
            })
          }
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
        >
          {busy === "shares" ? "Submitting..." : "Update shares"}
        </button>
      </section>
    </main>
  );
}
